/**
 * CardHudButton — a small floating card chip that surfaces the player's
 * classic-style card hand during the placement phase.
 *
 * Sized to match the existing controls, with a count in the top-right.
 * Placement with a valid set uses a static olive outline and faint
 * soft halo. Off-turn callers pass canTurnIn=false for the neutral card chip.
 * Mandatory turn-ins open the cards modal directly.
 *
 * Tapping opens the parent-supplied modal.
 */

import React from 'react'
import { TouchableOpacity, View, Platform, type StyleProp, type ViewStyle } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { CountBubble } from '../ui/CountBubble'
import { useTheme } from '../../hooks/useTheme'
import { CARD_READY_COLORS, CARD_READY_HALO_OPACITY } from '../../lib/cardHudAppearance'
export interface CardHudButtonProps {
  /** Cards in the viewer's hand. Drives the count badge. */
  handCount: number
  /** True when a 3-card valid set exists somewhere in hand. */
  canTurnIn: boolean
  /** True when the engine has flagged a mandatory turn-in (≥5 cards). */
  mustTurnIn: boolean
  onPress: () => void
  /** Smaller visual footprint for the persistent map-controls bar. */
  compact?: boolean
  /** Match the adjacent toolbar controls when compact. */
  compactSize?: 26 | 30
}

export function CardHudButton({
  handCount,
  canTurnIn,
  mustTurnIn,
  onPress,
  compact = false,
  compactSize = 30,
}: CardHudButtonProps) {
  return <View>
    {canTurnIn ? <CardReadyHudFace handCount={handCount} canTurnIn={canTurnIn} mustTurnIn={mustTurnIn}
      onPress={onPress} compact={compact} compactSize={compactSize} /> :
      <CardHudFace handCount={handCount} canTurnIn={canTurnIn} mustTurnIn={mustTurnIn}
        onPress={onPress} compact={compact} compactSize={compactSize} />}
  </View>
}

/** Selected Soft halo treatment: steady at half the proposal's maximum glow. */
function CardReadyHudFace(props: CardHudButtonProps) {
  const { theme } = useTheme()
  const colors = CARD_READY_COLORS[theme]
  return <CardHudFace {...props} style={{ backgroundColor: colors.paper, borderColor: colors.accent }}>
    <View testID="card-ready-halo" pointerEvents="none" accessible={false} style={{
      position: 'absolute', inset: -5, borderRadius: 10, borderWidth: 3, borderColor: colors.accent,
      opacity: CARD_READY_HALO_OPACITY,
      ...(Platform.OS === 'web' ? { boxShadow: `0 0 7px 2px ${colors.accent}` } : {
        shadowColor: colors.accent, shadowOpacity: 0.2, shadowRadius: 5, shadowOffset: { width: 0, height: 0 },
      }),
    }} />
  </CardHudFace>
}

/** Shared card icon, count and hit target for gameplay and QA treatments. */
export function CardHudFace({ handCount, canTurnIn, mustTurnIn, onPress, compact = false, compactSize = 30,
  style, badgeColor, children,
}: CardHudButtonProps & { style?: StyleProp<ViewStyle>; badgeColor?: string; children?: React.ReactNode }) {
  const { theme } = useTheme()
  const inkColor = theme === 'dark' ? '#e6e1c4' : '#18170f'
  const cornerInset = 1
  const BUBBLE_INSET = 2
  const borderClass = mustTurnIn || canTurnIn ? 'border-blood' : 'border-olive'
  const bgClass = mustTurnIn ? 'bg-blood/15' : 'bg-paper'
  return (
      <TouchableOpacity
        onPress={onPress}
        hitSlop={compact ? 4 : undefined}
        accessibilityRole="button"
        accessibilityLabel={
          mustTurnIn
            ? `Cards (${handCount}) — must turn in a set`
            : canTurnIn
              ? `Cards (${handCount}) — valid set available`
              : `Cards (${handCount})`
        }
        // A 1px outline, like the buttons around it (map bar, placement panel).
        className={`${bgClass} border ${borderClass} rounded-md items-center justify-center`}
        style={[
          {
            minHeight: compact ? compactSize : 36,
            minWidth: compact ? compactSize : 36,
            paddingHorizontal: compact ? 5 : 8,
            paddingVertical: compact ? 2 : 4,
            position: 'relative',
          },
          style,
        ]}
      >
        {/* The card pictogram — a monochrome, ink-tinted vector glyph so it
            reads dark on the cream light-mode button (the 🃏 emoji washed out). */}
        <MaterialCommunityIcons name="cards" size={compact ? 16 : 18} color={inkColor} />

        {/* Hand count: black (not red), so it reads as a count, not an
            unread badge; centred on the button corner. Always shown, 0
            included, so the hand size is never a guess. */}
        <View
          pointerEvents="none"
          // Zero-size anchor at the button's outer corner, a smidge in (down
          // and left): the bubble centres on it, whatever the number's width.
          style={{ position: 'absolute', top: BUBBLE_INSET - cornerInset, right: BUBBLE_INSET - cornerInset, width: 0, height: 0, alignItems: 'center', justifyContent: 'center', overflow: 'visible' }}
        >
          {/* The menu's notification bubble, in black: a count, not an alert. */}
          <CountBubble count={handCount} tone="count" fillColor={badgeColor} testID="card-hand-count" />
        </View>
        {children}
      </TouchableOpacity>
  )
}
